/**
 * Banking: a read-only copy of onlyfans.com/my/banking, answered by the CRM's
 * own GET /v1/banking/summary (crm/src/server/routes/banking.routes.js, owner
 * and super-admin only). Changing payout details needs OnlyFans' trusted flow
 * (face check / email code on the creator's side), so every edit opens
 * OnlyFans instead.
 */

export interface BankingField {
  name: string
  label: string
  value: string
  filled: boolean
}

export interface PayoutSystem {
  code: string
  title: string
  subtitle: string
  description: string
  minPayout: number | null
  payoutTime: string
  selected: boolean
  fields: BankingField[]
}

export type PayoutOption = Omit<PayoutSystem, 'fields'>

export interface BankingSummary {
  country: { code: string; name: string; canPay: boolean } | null
  status: {
    ivStatus: string
    approved: boolean
    rejectReason: string
    isCompany: boolean
    hideBanking: boolean
    canChangePayoutType: boolean
    w9Required: boolean
    w9Exists: boolean
  } | null
  payout: { filled: boolean; system: PayoutSystem | null } | null
  options: PayoutOption[] | null
  editUrl: string
  optionsUrl: string
  /** Which of the summary's OnlyFans reads failed (legal, bank, options, account). */
  errors: Record<string, string>
}

export const BANKING_EDIT_URL = 'https://onlyfans.com/my/banking/edit'
export const BANKING_OPTIONS_URL = 'https://onlyfans.com/my/banking/payout_types'

type Raw = Record<string, unknown>

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v))
const obj = (v: unknown): Raw | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : null)
/** Only OnlyFans links leave the console from this page. */
const ofUrl = (v: unknown, fallback: string) => (/^https:\/\/([a-z0-9-]+\.)*onlyfans\.com\//i.test(str(v)) ? str(v) : fallback)

function toOption(raw: unknown): PayoutOption | null {
  const o = obj(raw)
  if (!o) return null
  const min = Number(o.minPayout)
  return {
    code: str(o.code),
    title: str(o.title),
    subtitle: str(o.subtitle),
    description: str(o.description),
    minPayout: Number.isFinite(min) && min > 0 ? min : null,
    payoutTime: str(o.payoutTime),
    selected: o.selected === true,
  }
}

function toSystem(raw: unknown): PayoutSystem | null {
  const base = toOption(raw)
  if (!base) return null
  const fields = Array.isArray(obj(raw)?.fields) ? (obj(raw)!.fields as unknown[]) : []
  return {
    ...base,
    fields: fields.map(obj).filter((f): f is Raw => !!f).map((f) => ({ name: str(f.name), label: str(f.label), value: str(f.value), filled: f.filled === true })),
  }
}

/** The summary's `data`, tolerant of missing parts (each OnlyFans read may fail on its own). */
export function toBankingSummary(raw: unknown): BankingSummary {
  const d = obj(raw) ?? {}
  const country = obj(d.country)
  const status = obj(d.status)
  const payout = obj(d.payout)
  const errors = obj(d.errors) ?? {}
  return {
    country: country ? { code: str(country.code), name: str(country.name), canPay: country.canPay !== false } : null,
    status: status
      ? {
          ivStatus: str(status.ivStatus),
          approved: status.approved === true,
          rejectReason: str(status.rejectReason),
          isCompany: status.isCompany === true,
          hideBanking: status.hideBanking === true,
          canChangePayoutType: status.canChangePayoutType !== false,
          w9Required: status.w9Required === true,
          w9Exists: status.w9Exists === true,
        }
      : null,
    payout: payout ? { filled: payout.filled === true, system: toSystem(payout.system) } : null,
    options: Array.isArray(d.options) ? d.options.map(toOption).filter((o): o is PayoutOption => !!o) : null,
    editUrl: ofUrl(d.editUrl, BANKING_EDIT_URL),
    optionsUrl: ofUrl(d.optionsUrl, BANKING_OPTIONS_URL),
    errors: Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, str(v)])),
  }
}

/** "$20" / "$20.50" as OnlyFans shows a minimum payout. */
export function bankingMoney(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return `$${value % 1 ? value.toFixed(2) : value}`
}

/** "Bank transfer (ACH)" → "BANK TRANSFER" for the card heading. */
export function systemHeading(title: string): string {
  return title.replace(/\s*\(.*\)\s*$/, '').toUpperCase()
}
